import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../apiBase';
import { useLanguage } from '../../context/LanguageContext';
import {
  NOTIFY_KIND,
  bootstrapChatNotify,
  messageNotifySnippet,
  notifyIncomingChat,
  notifyOfficeEvent,
} from '../../lib/chatNotify';
import {
  isOwnedConnectionEvent,
  loadOwnedConnectionIds,
} from '../../lib/ownedConnections';

function getChatWsUrl() {
  const direct = (process.env.REACT_APP_WS_URL || '').trim().replace(/\/$/, '');
  if (direct) return direct.replace('https://', 'wss://').replace('http://', 'ws://');
  const base = (API_BASE_URL || '').trim();
  if (base) {
    try {
      const u = new URL(base);
      let path = (u.pathname || '/').replace(/\/+$/, '') || '/';
      if (path === '/api' || path.endsWith('/api')) {
        u.pathname = path.endsWith('/api') ? path.slice(0, -4) || '/' : '/';
      }
      u.pathname = (u.pathname || '/').replace(/\/+$/, '') || '/';
      const proto = u.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${proto}//${u.host}`;
    } catch {
      const stripped = base.replace(/\/$/, '').replace(/\/api$/i, '');
      return stripped.replace('https://', 'wss://').replace('http://', 'ws://');
    }
  }
  if (typeof window === 'undefined') return '';
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
}

function handleOfficeWsNotify(data, t) {
  if (!data || typeof data !== 'object') return;

  if (data.type === 'office_notify' && data.kind) {
    notifyOfficeEvent({
      kind: data.kind,
      title: data.title,
      body: data.body,
      actorName: data.actorName || data.actor_name,
      packName: data.packName || data.pack_name,
      credits: data.credits,
      amount: data.amount,
      dayTotal: data.dayTotal ?? data.day_total,
      entityId: data.entityId || data.entity_id,
      url: data.url,
      status: data.status,
      statusLabel: data.statusLabel || data.status_label,
      attendantName: data.attendantName || data.attendant_name,
      attendantId: data.attendantId || data.attendant_id,
      note: data.note,
      iconUrl: data.iconUrl || data.icon_url,
    });
    return;
  }

  if (data.type === 'attendance_updated') {
    notifyOfficeEvent({
      kind: NOTIFY_KIND.TEAM_ATTENDANCE,
      attendantId: data.attendant_id,
      attendantName: data.attendant_name,
      status: data.status,
      statusLabel: data.status_label,
      note: data.note,
      url: '/attendants-monitor',
    });
    return;
  }

  if (data.type === 'conversation_escalated') {
    notifyIncomingChat({
      notifyKind: NOTIFY_KIND.CHAT_ESCALATED,
      kind: 'alert',
      contactName: data.contact_name || 'Cliente',
      actorName: data.escalated_by_name || 'Atendente',
      reason: data.escalation_reason_label || '',
      conversationId: data.conversation_id != null ? String(data.conversation_id) : '',
      isOpenThread: false,
    });
    return;
  }

  if (data.type === 'escalation_resolved') {
    notifyIncomingChat({
      notifyKind: NOTIFY_KIND.CHAT_ESCALATION_RESOLVED,
      kind: 'alert',
      contactName: data.contact_name || 'Cliente',
      actorName: data.resolved_by_name || 'Admin',
      conversationId: data.conversation_id != null ? String(data.conversation_id) : '',
      isOpenThread: false,
    });
    return;
  }

  if (data.type !== 'new_message') return;
  if (data.muted) return;
  const msg = data.message;
  const convId = data.conversation_id != null ? String(data.conversation_id) : '';
  const avatar = ''; // Notificações usam a identidade do Office, sem fotos de contatos.
  const phone = data.contact_phone || '';
  if (msg?.is_from_contact) {
    notifyIncomingChat({
      kind: 'receive',
      notifyKind: NOTIFY_KIND.CHAT_MESSAGE,
      title: data.contact_name || t('chats.notifyNewMessage'),
      contactName: data.contact_name || t('chats.notifyNewMessage'),
      contactPhone: phone,
      contactProfilePicture: avatar,
      body: messageNotifySnippet(msg, t('chats.notifyAttachment')),
      conversationId: convId,
      isOpenThread: false,
    });
  } else if (msg?.sender_name === 'Sistema') {
    notifyIncomingChat({
      kind: 'alert',
      notifyKind: NOTIFY_KIND.CHAT_SYSTEM,
      title: data.contact_name || t('chats.notifyNewMessage'),
      contactName: data.contact_name || t('chats.notifyNewMessage'),
      contactPhone: phone,
      contactProfilePicture: avatar,
      body: messageNotifySnippet(msg, t('chats.notifyAlert')),
      conversationId: convId,
      isOpenThread: false,
    });
  }
}

/** Avisos em tempo real quando o utilizador não está na página Atendimentos. */
export default function ChatNotifyBridge() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const tRef = useRef(t);
  const onChats = location.pathname === '/chats' || location.pathname.startsWith('/chats/');

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    const onOpen = () => {
      if (location.pathname === '/chats' || location.pathname.startsWith('/chats/')) return;
      navigate('/chats');
    };
    const onNav = (ev) => {
      const raw = ev?.detail?.url;
      if (!raw) return;
      try {
        const u = new URL(String(raw), window.location.origin);
        if (u.origin !== window.location.origin) return;
        navigate(`${u.pathname}${u.search}`);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('aurax:open-chat', onOpen);
    window.addEventListener('aurax:notify-navigate', onNav);
    return () => {
      window.removeEventListener('aurax:open-chat', onOpen);
      window.removeEventListener('aurax:notify-navigate', onNav);
    };
  }, [location.pathname, navigate]);

  useEffect(() => {
    bootstrapChatNotify();
    let ownedIds = new Set();
    loadOwnedConnectionIds(true).then((ids) => {
      ownedIds = ids;
    });
    const refreshOwned = setInterval(() => {
      loadOwnedConnectionIds(true).then((ids) => {
        ownedIds = ids;
      });
    }, 60000);

    const wsUrl = getChatWsUrl();
    if (!wsUrl) {
      clearInterval(refreshOwned);
      return undefined;
    }

    let ws;
    let closed = false;
    let delay = 1000;
    let reconnectTimer;
    let heartbeatTimer;

    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(`${wsUrl}/ws`);
      } catch {
        reconnectTimer = setTimeout(connect, delay);
        delay = Math.min(delay * 2, 30000);
        return;
      }

      ws.onopen = () => {
        delay = 1000;
        loadOwnedConnectionIds(true).then((ids) => {
          ownedIds = ids;
        });
        clearInterval(heartbeatTimer);
        heartbeatTimer = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ type: 'ping' }));
            } catch {
              /* ignore */
            }
          }
        }, 20000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data?.type === 'pong') return;
          if (!isOwnedConnectionEvent(data, ownedIds)) return;
          // Fora de /chats: tudo. Em /chats: só equipa/comércio (mensagem fica no Chats.js).
          if (onChats) {
            if (
              data?.type === 'office_notify' ||
              data?.type === 'attendance_updated'
            ) {
              handleOfficeWsNotify(data, tRef.current);
            }
            return;
          }
          handleOfficeWsNotify(data, tRef.current);
        } catch {
          /* ignore */
        }
      };

      ws.onclose = () => {
        clearInterval(heartbeatTimer);
        if (closed) return;
        reconnectTimer = setTimeout(connect, delay);
        delay = Math.min(delay * 2, 30000);
      };
    };

    connect();

    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      clearInterval(heartbeatTimer);
      clearInterval(refreshOwned);
      if (ws) {
        ws.onclose = null;
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      }
    };
  }, [onChats]);

  return null;
}
